import React from 'react';
import { XApi } from '../services.js';
import { XShell } from '../shell.jsx';
import { XPrim } from '../primitives.jsx';

const TrackingContext = React.createContext({ payments: [], settledRevision: 0, trackPayment: () => {} });
const storageKey = (ownerId) => `xapi.stripe.pending.${ownerId}`;
const MAX_POLL_MS = 5 * 60_000;

function readPayments(ownerId) {
  try {
    const records = JSON.parse(sessionStorage.getItem(storageKey(ownerId)) || '[]');
    return Array.isArray(records) ? records.filter((record) =>
      record?.ownerId === ownerId && typeof record.sessionId === 'string' &&
      record.sessionId.startsWith('cs_') && Number.isFinite(record.startedAt) &&
      Date.now() - record.startedAt < 24 * 60 * 60_000) : [];
  } catch { return []; }
}

function PaymentPoller({ payment, onSettled, onInvalid, onTimeout }) {
  React.useEffect(() => {
    let cancelled = false;
    let timer;
    const check = async () => {
      try {
        const result = await XApi.stripeService.getSession(payment.sessionId);
        if (cancelled) return;
        if (!result?.pending && ['complete', 'reversed', 'partially_reversed', 'expired'].includes(result?.status)) {
          await onSettled(payment.sessionId, result);
          return;
        }
      } catch (error) {
        if (cancelled) return;
        if ([400, 401, 403, 404].includes(error?.status)) {
          onInvalid(payment.sessionId);
          return;
        }
      }
      if (cancelled) return;
      if (Date.now() - payment.startedAt < MAX_POLL_MS) timer = setTimeout(check, 10_000);
      else onTimeout(payment.sessionId);
    };
    void check();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [payment.sessionId, payment.startedAt, onSettled, onInvalid, onTimeout]);
  return null;
}

// Mounted above the page router so navigation does not interrupt payment recovery.
export function StripePaymentTrackingProvider({ children }) {
  const { authed, user, refreshMe } = XShell.useAuth();
  const { toast } = XPrim.useToast();
  const toastRef = React.useRef(toast);
  toastRef.current = toast;
  const ownerId = authed ? user?._raw?.id || user?.id || null : null;
  const ownerRef = React.useRef(ownerId);
  ownerRef.current = ownerId;
  const [state, setState] = React.useState({ ownerId: null, payments: [] });
  const [settledRevision, setSettledRevision] = React.useState(0);
  const payments = state.ownerId === ownerId ? state.payments : [];

  React.useEffect(() => {
    setState({ ownerId, payments: ownerId ? readPayments(ownerId) : [] });
  }, [ownerId]);

  React.useEffect(() => {
    if (ownerId && state.ownerId === ownerId) {
      try { sessionStorage.setItem(storageKey(ownerId), JSON.stringify(state.payments)); } catch {}
    }
  }, [ownerId, state]);

  const trackPayment = React.useCallback((sessionId) => {
    if (!ownerId || ownerRef.current !== ownerId || !sessionId?.startsWith('cs_')) return;
    setState((previous) => {
      const current = previous.ownerId === ownerId ? previous.payments : readPayments(ownerId);
      if (current.some((payment) => payment.sessionId === sessionId)) return { ownerId, payments: current };
      return { ownerId, payments: [...current, { ownerId, sessionId, startedAt: Date.now(), timedOut: false }] };
    });
  }, [ownerId]);

  const removePayment = React.useCallback((sessionId) => {
    if (ownerRef.current !== ownerId) return;
    setState((previous) => previous.ownerId === ownerId
      ? { ...previous, payments: previous.payments.filter((payment) => payment.sessionId !== sessionId) }
      : previous);
  }, [ownerId]);

  const onSettled = React.useCallback(async (sessionId, result) => {
    if (ownerRef.current !== ownerId) return;
    const refreshed = await refreshMe();
    if (ownerRef.current !== ownerId || (refreshed?._raw?.id || refreshed?.id) !== ownerId) return;
    removePayment(sessionId);
    setSettledRevision((value) => value + 1);
    toastRef.current(result.status === 'complete'
      ? 'Payment confirmed; your balance has been updated'
      : 'Payment status changed; check your billing history');
  }, [ownerId, refreshMe, removePayment]);

  const onTimeout = React.useCallback((sessionId) => {
    if (ownerRef.current !== ownerId) return;
    setState((previous) => previous.ownerId === ownerId
      ? { ...previous, payments: previous.payments.map((payment) => payment.sessionId === sessionId
        ? { ...payment, timedOut: true } : payment) }
      : previous);
  }, [ownerId]);

  return <TrackingContext.Provider value={{ payments, settledRevision, trackPayment }}>
    {payments.map((payment) => <PaymentPoller key={`${ownerId}:${payment.sessionId}`} payment={payment}
      onSettled={onSettled} onInvalid={removePayment} onTimeout={onTimeout} />)}
    {children}
  </TrackingContext.Provider>;
}

export const useStripePaymentTracking = () => React.useContext(TrackingContext);
